import React, { useState, useRef, ReactNode, useEffect } from 'react';
import classnames from 'classnames';
import GlobalProps from '../../globalProps';
import { useForm } from '../Form';
import { Icon } from '../../Icon/Icon';
import { IconName } from '../../Icon/IconName';
import './Select.css';
import { t } from '../../../locales/translate';
import { Spinner } from '../../Spinner/Spinner';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';

export interface Option {
  value: string;
  label?: string;
  disabled?: boolean;
}
interface Props extends GlobalProps {
  name: string;
  label: string;
  options?: Option[];
  icon?: IconName;
  actions?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  description?: string;
  errorMessage?: string;
  allowEmpty?: boolean | string;
  defaultValue?: string | null;
  loading?: boolean;
  loadingMsg?: string;
  displayWithoutOptions?: boolean;
}

export function Select({
  name,
  label,
  required,
  disabled,
  readonly,
  options,
  icon,
  actions,
  description,
  errorMessage,
  className,
  style,
  allowEmpty,
  defaultValue,
  loading,
  loadingMsg,
  displayWithoutOptions,
}: Props): JSX.Element {
  const [value, setValue, triedToSubmit, reportValidationStatus] = useForm<string | undefined>(name);
  const [active, setActive] = useState(false);

  const inputRef: React.MutableRefObject<HTMLSelectElement | null> = useRef(null);

  useEffect(() => {
    if (!value && defaultValue !== undefined && options) {
      const foundOption = options.find((option) => option.value === defaultValue);
      if (foundOption) {
        setValue(foundOption.value);
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      reportValidationStatus(true);
    };
  }, []);

  const hasValue = !!value || typeof allowEmpty === 'string';

  useEffect(() => {
    if (required) {
      reportValidationStatus(hasValue);
    }
  }, [required, hasValue]);

  const validationMessage =
    errorMessage || (triedToSubmit && inputRef.current && inputRef.current.validationMessage) || undefined;

  if (loading) {
    return <Spinner color="red" text={loadingMsg || t.actions.loading('')} />;
  } else if (!displayWithoutOptions && (!options || options.length === 0)) {
    return <></>;
  }

  return (
    <div className={classnames('select', 'input', { 'input--with-icon': icon }, className)} style={style}>
      {icon && <Icon name={icon} className="input__icon" />}
      <div
        className={classnames('input__container', {
          'input__container--active': active,
          'input__container--full': hasValue || active,
          'input__container--error': validationMessage,
        })}
      >
        <div className="input__input-wrapper">
          {label && (
            <label className="input__label" htmlFor={name}>
              {label}
              {!required && ` (${t.general.optional})`}
            </label>
          )}
          <div className="select__select-wrapper">
            <select
              name={name}
              id={name}
              className="input__input"
              onChange={(v) => {
                setValue(v.target.value);
              }}
              value={value ?? ''}
              onFocus={() => {
                setActive(true);
              }}
              onBlur={() => {
                setActive(false);
              }}
              required={required}
              disabled={disabled || readonly}
              ref={inputRef}
            >
              {allowEmpty && <option value="">{allowEmpty}</option>}
              {options &&
                options.map((option) => {
                  return (
                    <option key={option.value} value={option.value} disabled={option.disabled}>
                      {option.label ? option.label : option.value}
                    </option>
                  );
                })}
            </select>

            <Icon className="select__arrow-down" name="arrow-triangle-down--sm" />
          </div>
          {actions && <div className="input__actions">{actions}</div>}
          {validationMessage && <Icon name="alert-circle--sm" className="input__alert-circle" />}
        </div>
        <div className="input__description-wrapper">
          {description && <div className="input__description">{description}</div>}
          <Collapse open={validationMessage ? true : false}>
            <div className="input__error-message">{validationMessage}</div>
          </Collapse>
        </div>
      </div>
    </div>
  );
}
